import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  cancelManageAppointment,
  getManageAppointment,
  getManageAvailability,
  rescheduleManageAppointment,
} from "../../api/appointmentManage";
import {
  formatInTimeZone,
  parseLocalDateTimeInTimeZone,
} from "../../utils/datetime";

const formatLocalDateTime = (value, tz) => {
  if (!value || !tz) return value || "";
  const dateObj = parseLocalDateTimeInTimeZone(value, tz);
  if (!dateObj) return value;
  return formatInTimeZone(
    dateObj,
    tz,
    {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    },
    "en-US"
  );
};

const buildDefaultDate = (appointment) => {
  const local = appointment?.start_time_local;
  if (!local) return "";
  return local.split("T")[0] || "";
};

const formatTimeLabel = (value, tz) => {
  if (!value || !tz) return value || "";
  const dateObj = parseLocalDateTimeInTimeZone(value, tz);
  if (!dateObj) return value;
  return formatInTimeZone(
    dateObj,
    tz,
    { hour: "numeric", minute: "2-digit" },
    "en-US"
  );
};

export default function PortalManageAppointment() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [actionError, setActionError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);

  const tzName = data?.timezone || "";
  const appointment = data?.appointment || null;
  const salon = data?.salon || {};
  const actions = data?.actions || {};
  const cancelAction = actions?.cancel || {};
  const rescheduleAction = actions?.reschedule || {};

  const loadAppointment = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const response = await getManageAppointment(token);
      setData(response);
      setStatus("idle");
      setReason("");
      setNote("");
      setActionError("");
      const defaultDate = buildDefaultDate(response?.appointment);
      setSelectedDate(defaultDate);
      setSelectedSlot(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load appointment.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAppointment();
  }, [loadAppointment]);

  const fetchSlots = useCallback(
    async (dateValue) => {
      if (!dateValue || !token || !rescheduleAction?.allowed) return;
      setSlotsLoading(true);
      setSlotsError("");
      try {
        const response = await getManageAvailability({
          token,
          date: dateValue,
        });
        const list = Array.isArray(response?.slots) ? response.slots : [];
        setSlots(list);
        setSelectedSlot(list[0] || null);
      } catch (err) {
        setSlots([]);
        setSelectedSlot(null);
        setSlotsError(
          err?.response?.data?.detail || "Unable to load available times."
        );
      } finally {
        setSlotsLoading(false);
      }
    },
    [token, rescheduleAction?.allowed]
  );

  useEffect(() => {
    if (!selectedDate) return;
    fetchSlots(selectedDate);
  }, [selectedDate, fetchSlots]);

  const handleCancel = async () => {
    if (!token || !cancelAction?.allowed) return;
    if (data?.require_reason_on_cancel && !reason) {
      setActionError("Please choose a reason for cancellation.");
      return;
    }
    setSubmitting(true);
    setActionError("");
    try {
      const result = await cancelManageAppointment(token, { reason, note });
      setStatus("cancelled");
      setData((prev) => ({ ...prev, appointment: result?.appointment || appointment }));
    } catch (err) {
      setActionError(
        err?.response?.data?.detail || "Unable to cancel this appointment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReschedule = async () => {
    if (!token || !rescheduleAction?.allowed || !selectedSlot) return;
    setSubmitting(true);
    setActionError("");
    try {
      const result = await rescheduleManageAppointment(token, {
        new_start_at_local: selectedSlot.start_time_local,
      });
      setStatus("rescheduled");
      setData((prev) => ({ ...prev, appointment: result?.appointment || appointment }));
    } catch (err) {
      setActionError(
        err?.response?.data?.detail || "Unable to reschedule this appointment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const appointmentDateLabel = useMemo(() => {
    if (!appointment?.start_time_local || !tzName) return "";
    return formatLocalDateTime(appointment.start_time_local, tzName);
  }, [appointment?.start_time_local, tzName]);

  const contactPhone = salon?.phone || "";
  const callLink = contactPhone ? `tel:${contactPhone}` : null;
  const isTokenActive = data?.token_status === "active";
  const showLateMessage = !cancelAction?.allowed && !rescheduleAction?.allowed;

  return (
    <div className="px-4 py-6">
      <div className="mx-auto w-full max-w-[1200px] text-base md:text-lg">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Manage appointment
            </h1>
            <p className="text-sm md:text-base text-rose-100">
              Cancel or reschedule from a secure link.
            </p>
          </div>
          <div className="px-6 py-8 space-y-6">
            {loading && (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-600">
                Loading appointment details...
              </div>
            )}
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}

            {!loading && !error && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                          Appointment summary
                        </h2>
                        <p className="text-sm text-slate-600">
                          {salon?.name || "Salon"} · {salon?.address || "Address on file"}
                        </p>
                        {salon?.phone && (
                          <p className="text-sm text-slate-600">
                            Phone: {salon.phone}
                          </p>
                        )}
                      </div>
                      {data?.token_expires_at_local && (
                        <div className="text-xs text-slate-500">
                          Link expires {formatLocalDateTime(data.token_expires_at_local, tzName)}
                        </div>
                      )}
                    </div>

                    {appointment ? (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2 text-sm">
                        <div className="text-slate-700">
                          <span className="font-semibold">Client:</span>{" "}
                          {appointment.client_name || "Client"}
                        </div>
                        <div className="text-slate-700">
                          <span className="font-semibold">Date & time:</span>{" "}
                          {appointmentDateLabel || appointment.start_time_local}
                        </div>
                        <div className="text-slate-700">
                          <span className="font-semibold">Technician:</span>{" "}
                          {appointment.technician_name || "Assigned staff"}
                        </div>
                        <div className="text-slate-700">
                          <span className="font-semibold">Services:</span>{" "}
                          {appointment.services_summary || "Service"}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                        Appointment details are not available.
                      </div>
                    )}

                    {data?.policy_text && (
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                        {data.policy_text}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                    <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                      What would you like to do?
                    </h2>
                    {!isTokenActive && (
                      <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        This link is no longer available. Please contact the salon for help.
                      </div>
                    )}
                    {data?.preview_only && (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                        This is a preview. Actions are disabled.
                      </div>
                    )}
                    {showLateMessage && data?.late_action_text && (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                        {data.late_action_text}
                      </div>
                    )}
                    {callLink && showLateMessage && (
                      <a
                        href={callLink}
                        className="inline-flex items-center justify-center rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600"
                      >
                        Call salon
                      </a>
                    )}
                  </div>
                </div>

                {status === "cancelled" && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                    Appointment cancelled. If you need anything else, contact the salon.
                  </div>
                )}
                {status === "rescheduled" && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                    Appointment rescheduled. A confirmation will be sent if enabled.
                  </div>
                )}

                {actionError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                    {actionError}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-slate-900">
                        Cancel appointment
                      </h3>
                      {!cancelAction?.allowed && (
                        <span className="text-xs text-slate-500">Not available</span>
                      )}
                    </div>
                    {cancelAction?.allowed && (
                      <>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">
                            Reason
                          </label>
                          <select
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm"
                          >
                            <option value="">Select a reason</option>
                            {(data?.reason_options || []).map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">
                            Note (optional)
                          </label>
                          <textarea
                            rows={3}
                            value={note}
                            onChange={(event) => setNote(event.target.value)}
                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleCancel}
                          disabled={submitting || data?.preview_only}
                          className="inline-flex items-center rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                        >
                          {submitting ? "Cancelling..." : "Cancel appointment"}
                        </button>
                      </>
                    )}
                    {cancelAction?.blocked_reason && !cancelAction?.allowed && (
                      <div className="text-sm text-slate-600">
                        {cancelAction.blocked_reason}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-slate-900">
                        Reschedule appointment
                      </h3>
                      {!rescheduleAction?.allowed && (
                        <span className="text-xs text-slate-500">Not available</span>
                      )}
                    </div>
                    {rescheduleAction?.allowed && (
                      <>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">
                            Pick a date
                          </label>
                          <input
                            type="date"
                            value={selectedDate}
                            onChange={(event) => setSelectedDate(event.target.value)}
                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">
                            Available times
                          </label>
                          {slotsLoading ? (
                            <div className="text-sm text-slate-500">Loading times...</div>
                          ) : slotsError ? (
                            <div className="text-sm text-rose-600">{slotsError}</div>
                          ) : slots.length === 0 ? (
                            <div className="text-sm text-slate-500">
                              No available times for this date.
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {slots.map((slot) => (
                                <button
                                  key={slot.start_time_local}
                                  type="button"
                                  onClick={() => setSelectedSlot(slot)}
                                  className={`rounded-full px-3 py-2 text-sm font-semibold border ${
                                    selectedSlot?.start_time_local === slot.start_time_local
                                      ? "border-rose-500 bg-rose-50 text-rose-600"
                                      : "border-slate-300 text-slate-700 hover:bg-slate-50"
                                  }`}
                                >
                                  {formatTimeLabel(slot.start_time_local, tzName)}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleReschedule}
                          disabled={submitting || !selectedSlot || data?.preview_only}
                          className="inline-flex items-center rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                        >
                          {submitting ? "Rescheduling..." : "Confirm reschedule"}
                        </button>
                      </>
                    )}
                    {rescheduleAction?.blocked_reason && !rescheduleAction?.allowed && (
                      <div className="text-sm text-slate-600">
                        {rescheduleAction.blocked_reason}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
